import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '@/components/layout';
import { Button, Card, Chip, EmptyState, Skeleton } from '@/components/ui';
import { sceneImage } from '@/lib/illustrations';
import { useT } from '@/i18n';
import { useSession } from '@/hooks/session';
import { formatMoney } from '@/lib/format';
import { api } from '@/api/client';
import type { Child, ReportSummary, Subscription } from '@/api/types';
import { groupSessions } from '@/features/session/groupSessions';
import { SessionCard } from '@/features/session/SessionCard';

const SKELETON_CARD_COUNT = 3;

export const MyReports = () => {
  const t = useT();
  const navigate = useNavigate();
  const { config } = useSession();
  const [reports, setReports] = useState<ReportSummary[]>([]);
  const [children, setChildren] = useState<Child[]>([]);
  const [loading, setLoading] = useState(true);
  const [childId, setChildId] = useState<string | undefined>(undefined);
  const [subscription, setSubscription] = useState<Subscription | null>(null);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([api.reports.list(), api.children.list()])
      .then(([reportsRes, childrenRes]) => {
        if (cancelled) return;
        setReports(reportsRes.reports);
        setChildren(childrenRes.children);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Nurture Plus is only on offer where `config.plans.plus` is set (India, for now) — skip the
  // lookup elsewhere rather than asking the API a question that can't apply.
  const hasPlusPlan = !!config?.plans.plus;
  useEffect(() => {
    if (!hasPlusPlan) return undefined;
    let cancelled = false;
    void api.subscriptions
      .current()
      .then(({ subscription: sub }) => {
        if (!cancelled) setSubscription(sub ?? null);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [hasPlusPlan]);

  const pricing = config?.pricing.IN;
  const price = pricing ? formatMoney(pricing.launchAmount, pricing.currency) : undefined;

  const sessions = useMemo(() => groupSessions(reports), [reports]);
  const visibleSessions = useMemo(
    () => (childId ? sessions.filter((session) => session.childId === childId) : sessions),
    [sessions, childId],
  );

  const goToChildren = () => navigate('/children');

  return (
    <AppShell>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-h1 text-ink">{t('reports.title')}</h1>
        <Button variant="secondary" onClick={goToChildren}>
          {t('reports.start')}
        </Button>
      </div>

      {children.length > 0 && (
        <div role="radiogroup" aria-label={t('child.pick')} className="mt-4 flex flex-wrap gap-2">
          <Chip role="radio" selected={childId === undefined} onToggle={() => setChildId(undefined)}>
            {t('reports.allChildren')}
          </Chip>
          {children.map((child) => (
            <Chip
              key={child.childId}
              role="radio"
              selected={childId === child.childId}
              onToggle={() => setChildId(child.childId)}
            >
              {child.name}
            </Chip>
          ))}
        </div>
      )}

      <div className="mt-6 space-y-4">
        {loading ? (
          Array.from({ length: SKELETON_CARD_COUNT }, (_, i) => (
            <Card key={i}>
              <Skeleton lines={3} />
            </Card>
          ))
        ) : visibleSessions.length === 0 ? (
          <EmptyState
            image={sceneImage('empty-reports')}
            title={t('reports.empty')}
            body={t('reports.emptyBody')}
            action={<Button onClick={goToChildren}>{t('reports.start')}</Button>}
          />
        ) : (
          visibleSessions.map((session) => (
            <SessionCard key={session.assessmentId} session={session} price={price} subscription={subscription} />
          ))
        )}
      </div>
    </AppShell>
  );
};
